import { authService } from '@/api';
import { Button, Typography } from '@/components/common';
import { Progress } from '@/components/ui/progress';
import { useNotification, useUser } from '@/hooks';
import * as React from 'react';

// CLAIM-3486 — this banner now only tracks email verification. It used to
// also cover profile completion (first/last name), but that requirement was
// dropped from onboarding, so `profile_completion_date` is no longer read
// here — see PersonalDetails.tsx / usePersonalInformation.tsx for the same
// change on the Settings side.
const VERIFICATION_PROGRESS_VALUE = 90;

export const CompleteProfileBanner = (): React.JSX.Element => {
	const { user } = useUser();
	const { toastSuccess, toastError } = useNotification();
	const [isResending, setIsResending] = React.useState(false);

	const handleResendEmail = async (): Promise<void> => {
		if (!user?.email || isResending) return;
		setIsResending(true);
		try {
			await authService.resendVerificationEmail(user.email);
			toastSuccess('Verification email sent. Please check your inbox.');
		} catch (error) {
			toastError('Something went wrong. Please try again.');
		} finally {
			setIsResending(false);
		}
	};

	return (
		<>
			{user && !user.is_verified ? (
				<div className="bg-white flex flex-col gap-y-5 px-[18px] lg:px-6 py-[18px] border border-grey-100 rounded-3xl">
					<div className="flex flex-col-reverse lg:flex-row justify-between w-full gap-2 items-center">
						<div className="space-y-1">
							<Typography type="semibold-16" className="text-black">
								Verify Your Email Address
							</Typography>
							<Typography type="regular-16" className="text-opacity-black">
								Please check your inbox to confirm your account and continue.
							</Typography>
						</div>
						<Button
							label="Resend email"
							size="fit"
							decoration="primary"
							loading={isResending}
							disabled={isResending}
							onClick={handleResendEmail}
						/>
					</div>
					<Progress
						value={VERIFICATION_PROGRESS_VALUE}
						classNameRoot="bg-grey-10 !h-2.5"
						classNameIndicator="bg-primary rounded-full"
					/>
				</div>
			) : null}
		</>
	);
};
